"use client";

import React, { useState, useEffect } from "react";
import { Ruler, Sparkles, Mail, Check, CircleQuestionMark as HelpCircle } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
const useWvcLanguage = () => {
  const [lang, setLang] = useState<"en" | "zh" | "fr" | "es" | "de">("en");
  useEffect(() => {
    const detectLang = () => {
      const htmlLang = document.documentElement.lang?.toLowerCase() || "en";
      if (htmlLang.startsWith("zh")) return "zh";
      if (htmlLang.startsWith("fr")) return "fr";
      if (htmlLang.startsWith("es")) return "es";
      if (htmlLang.startsWith("de")) return "de";
      return "en";
    };
    setLang(detectLang());
    const observer = new MutationObserver(() => {
      setLang(detectLang());
    });
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["lang"]
    });
    return () => observer.disconnect();
  }, []);
  return lang;
};
const translations = {
  en: {
    international: "International standard",
    sizeGuide: "Size & fit",
    guide: "guide",
    description: "Our silhouettes are tailored with intentional drape and proportion. Consult our conversions below or speak with our concierge team.",
    inches: "Inches",
    centimeters: "Centimeters",
    measurements: "Garment body measurements",
    showing: "Showing measurements in",
    inCentimeters: "centimeters (cm)",
    inInches: "inches (in)",
    standardFit: "Standard tailored fit",
    size: "Size",
    us: "US",
    uk: "UK",
    eu: "EU",
    bust: "Bust",
    waist: "Waist",
    hips: "Hips",
    length: "Avg. Length",
    betweenSizes: "Between sizes",
    betweenSizesText: "If your bust and waist fall between two brackets, we suggest sizing down for structured tailoring or up for fluid, relaxed drapes.",
    fabricConsideration: "Fabric consideration",
    fabricText: "Items crafted from pure mulberry silk and crisp poplin carry zero stretch; knitwear adapts naturally to contours.",
    howToMeasure: "How to measure",
    measureText: "Use a flexible fabric measuring tape held level and firm against the body.",
    bust1: "1. Bust",
    bustDesc: "Measure around the fullest part of your chest, keeping the tape snug but not tight.",
    waist1: "2. Natural waist",
    waistDesc: "Locate your natural waist (above your hip bone) and measure snugly around this narrowest part.",
    hip1: "3. Fullest hip",
    hipDesc: "Stand with feet together and measure around the fullest part of your hip and buttocks.",
    length1: "4. Garment length",
    lengthDesc: "For dresses and tunics, measure from your shoulder point to your desired hem length.",
    atelierAssistance: "Atelier assistance",
    needGuidance: "Need bespoke sizing guidance?",
    atelierText: "Our private client styling team is available to assist with exact garment specs, cross-category sizing, and hem alterations.",
    emailConcierge: "Email concierge stylist",
    viewFaqs: "View customer service FAQs"
  },
  zh: {
    international: "国际标准",
    sizeGuide: "尺码和合身",
    guide: "指南",
    description: "我们的剪裁具有有意的褶皱和比例。请查阅下面的转换或与我们的礼宾团队联系。",
    inches: "英寸",
    centimeters: "厘米",
    measurements: "服装尺寸",
    showing: "显示测量值",
    inCentimeters: "厘米 (厘米)",
    inInches: "英寸 (英寸)",
    standardFit: "标准裁剪合身",
    size: "尺码",
    us: "美国",
    uk: "英国",
    eu: "欧盟",
    bust: "胸部",
    waist: "腰部",
    hips: "臀部",
    length: "平均长度",
    betweenSizes: "尺码之间",
    betweenSizesText: "如果您的胸部和腰部在两个括号之间，我们建议对于结构化裁剪向下调整尺码，对于流体、放松的褶皱向上调整。",
    fabricConsideration: "织物考虑",
    fabricText: "用纯桑蚕丝和脆爽棉布制作的物品没有拉伸；针织品自然适应轮廓。",
    howToMeasure: "如何测量",
    measureText: "使用灵活的织物测量带，保持水平并紧贴身体。",
    bust1: "1. 胸部",
    bustDesc: "在胸部最丰满的地方测量，保持测量带贴身但不紧。",
    waist1: "2. 自然腰围",
    waistDesc: "找到您的自然腰部（髋骨上方），并在这个最狭窄的部分周围紧紧测量。",
    hip1: "3. 最丰满的臀部",
    hipDesc: "并脚站立，并测量臀部和臀部最丰满的部分。",
    length1: "4. 服装长度",
    lengthDesc: "对于连衣裙和调谐器，从肩点测量到所需的下摆长度。",
    atelierAssistance: "工作室协助",
    needGuidance: "需要定制尺码指导吗？",
    atelierText: "我们的私人客户造型团队可以帮助您进行精确的服装规格、跨类别尺码和下摆修改。",
    emailConcierge: "电子邮件礼宾造型师",
    viewFaqs: "查看客户服务常见问题"
  },
  fr: {
    international: "Norme Internationale",
    sizeGuide: "Taille et Ajustement",
    guide: "guide",
    description: "Nos silhouettes sont taillées avec une draperie et une proportion intentionnelles. Consultez nos conversions ci-dessous ou parlez à notre équipe de concierge.",
    inches: "Pouces",
    centimeters: "Centimètres",
    measurements: "Mesures de Garment Body",
    showing: "Montrant les mesures dans",
    inCentimeters: "centimètres (cm)",
    inInches: "pouces (po)",
    standardFit: "Ajustement Taillé Standard",
    size: "Taille",
    us: "US",
    uk: "UK",
    eu: "UE",
    bust: "Poitrine",
    waist: "Taille",
    hips: "Hanches",
    length: "Longueur Moyenne",
    betweenSizes: "Entre les Tailles",
    betweenSizesText: "Si votre poitrine et votre taille se situent entre deux crochets, nous vous suggérons de réduire la taille pour une tailleur structuré ou d'augmenter pour des drapés fluides et détendus.",
    fabricConsideration: "Considération de Tissu",
    fabricText: "Les articles fabriqués à partir de soie de mûrier pur et de popeline croustillante ne présentent aucun étirement; les tricots s'adaptent naturellement aux contours.",
    howToMeasure: "Comment Mesurer",
    measureText: "Utilisez un ruban à mesurer en tissu flexible maintenu au niveau et ferme contre le corps.",
    bust1: "1. Poitrine",
    bustDesc: "Mesurez autour de la partie la plus pleine de votre poitrine, en gardant le ruban ajusté mais pas serré.",
    waist1: "2. Taille Naturelle",
    waistDesc: "Localisez votre taille naturelle (au-dessus de votre os iliaque) et mesurez confortablement autour de cette partie la plus étroite.",
    hip1: "3. Hanche la Plus Pleine",
    hipDesc: "Tenez-vous debout les pieds ensemble et mesurez autour de la partie la plus pleine de votre hanche et de vos fesses.",
    length1: "4. Longueur de Garment",
    lengthDesc: "Pour les robes et tuniques, mesurez du point de votre épaule à la longueur d'ourlet souhaitée.",
    atelierAssistance: "Assistance Atelier",
    needGuidance: "Besoin d'un conseil de dimensionnement sur mesure?",
    atelierText: "Notre équipe de style client privé est disponible pour vous aider avec les spécifications exactes des vêtements, le dimensionnement entre catégories et les modifications d'ourlet.",
    emailConcierge: "Styliste concierge par email",
    viewFaqs: "Voir les FAQ du service client"
  },
  es: {
    international: "Estándar Internacional",
    sizeGuide: "Tamaño y Ajuste",
    guide: "guía",
    description: "Nuestras siluetas están confeccionadas con caída e proporción intencional. Consulte nuestras conversiones a continuación o hable con nuestro equipo de conserjería.",
    inches: "Pulgadas",
    centimeters: "Centímetros",
    measurements: "Medidas del Cuerpo de la Prenda",
    showing: "Mostrando medidas en",
    inCentimeters: "centímetros (cm)",
    inInches: "pulgadas (pulg)",
    standardFit: "Ajuste Estándar Personalizado",
    size: "Tamaño",
    us: "US",
    uk: "UK",
    eu: "UE",
    bust: "Bust",
    waist: "Cintura",
    hips: "Caderas",
    length: "Longitud Promedio",
    betweenSizes: "Entre Tallas",
    betweenSizesText: "Si tu busto y cintura caen entre dos corchetes, te sugerimos reducir el tamaño para un traje estructurado o aumentar para drapeos fluidos y relajados.",
    fabricConsideration: "Consideración de Tela",
    fabricText: "Los artículos fabricados con seda de morera pura y popelina crujiente no tienen elasticidad; la ropa de punto se adapta naturalmente a los contornos.",
    howToMeasure: "Cómo Medir",
    measureText: "Usa una cinta métrica de tela flexible mantenida a nivel y firme contra el cuerpo.",
    bust1: "1. Busto",
    bustDesc: "Mide alrededor de la parte más completa de tu pecho, manteniendo la cinta ceñida pero no apretada.",
    waist1: "2. Cintura Natural",
    waistDesc: "Localiza tu cintura natural (encima de tu cadera) y mide cómodamente alrededor de esta parte más estrecha.",
    hip1: "3. Cadera Más Completa",
    hipDesc: "Párate con los pies juntos y mide alrededor de la parte más completa de tu cadera y nalgas.",
    length1: "4. Longitud de la Prenda",
    lengthDesc: "Para vestidos y túnicas, mide desde el punto del hombro hasta la longitud del dobladillo deseado.",
    atelierAssistance: "Asistencia de Atelier",
    needGuidance: "¿Necesitas guía de dimensionamiento personalizado?",
    atelierText: "Nuestro equipo de estilo de cliente privado está disponible para ayudarlo con especificaciones exactas de prendas, dimensionamiento entre categorías y alteraciones de dobladillo.",
    emailConcierge: "Estilista concierge por correo electrónico",
    viewFaqs: "Ver preguntas frecuentes del servicio al cliente"
  },
  de: {
    international: "Internationale Norm",
    sizeGuide: "Größe und Sitz",
    guide: "Anleitung",
    description: "Unsere Silhouetten sind mit absichtlicher Drapierung und Proportion zugeschnitten. Beachten Sie unsere Konvertierungen unten oder sprechen Sie mit unserem Concierge-Team.",
    inches: "Zoll",
    centimeters: "Zentimeter",
    measurements: "Körpermessungen der Kleidung",
    showing: "Messungen anzeigen in",
    inCentimeters: "Zentimeter (cm)",
    inInches: "Zoll (in)",
    standardFit: "Standard-Maßanpassung",
    size: "Größe",
    us: "US",
    uk: "UK",
    eu: "EU",
    bust: "Brust",
    waist: "Taille",
    hips: "Hüften",
    length: "Durchschnittliche Länge",
    betweenSizes: "Zwischen Größen",
    betweenSizesText: "Wenn Ihre Brust und Taille zwischen zwei Klammern liegen, empfehlen wir Ihnen, die Größe für strukturierte Schneiderei zu verringern oder für fließende, lockere Drapierungen zu vergrößern.",
    fabricConsideration: "Stoffüberlegung",
    fabricText: "Aus reiner Maulbeerseiden- und knackiger Popeline gefertigte Artikel haben keinen Dehnungsspielraum; Strickwaren passen sich natürlich an die Konturen an.",
    howToMeasure: "So messen Sie",
    measureText: "Verwenden Sie ein flexibles Stoffmessband, das eben und fest gegen den Körper gehalten wird.",
    bust1: "1. Brust",
    bustDesc: "Messen Sie um den vollsten Teil Ihrer Brust, wobei Sie das Band eng, aber nicht fest halten.",
    waist1: "2. Natürliche Taille",
    waistDesc: "Lokalisieren Sie Ihre natürliche Taille (über Ihrem Hüftknochen) und messen Sie bequem um diesen engsten Teil herum.",
    hip1: "3. Vollste Hüfte",
    hipDesc: "Stellen Sie sich mit zusammen stehenden Füßen hin und messen Sie um den vollsten Teil Ihrer Hüfte und Gesäß.",
    length1: "4. Kleidungslänge",
    lengthDesc: "Messen Sie bei Kleidern und Tuniken von Ihrem Schulterpunkt bis zur gewünschten Saumlänge.",
    atelierAssistance: "Atelierassistenz",
    needGuidance: "Benötigen Sie maßgeschneiderte Größenberatung?",
    atelierText: "Unser Private-Client-Styling-Team kann Ihnen mit exakten Kleidungsspezifikationen, kategorieübergreifender Größenbestimmung und Saumanpassungen helfen.",
    emailConcierge: "E-Mail-Concierge-Stylist",
    viewFaqs: "FAQ zum Kundenservice anzeigen"
  }
};
interface SizeRow {
  size: string;
  us: string;
  uk: string;
  eu: string;
  bustIn: string;
  bustCm: string;
  waistIn: string;
  waistCm: string;
  hipsIn: string;
  hipsCm: string;
  lengthIn: string;
  lengthCm: string;
}
const sizeData: SizeRow[] = [{
  size: "XS",
  us: "0 – 2",
  uk: "4 – 6",
  eu: "32 – 34",
  bustIn: "31.5 – 32.5",
  bustCm: "80 – 83",
  waistIn: "24.0 – 25.0",
  waistCm: "61 – 64",
  hipsIn: "34.5 – 35.5",
  hipsCm: "87 – 90",
  lengthIn: "44.0",
  lengthCm: "112"
}, {
  size: "S",
  us: "4 – 6",
  uk: "8 – 10",
  eu: "36 – 38",
  bustIn: "33.5 – 34.5",
  bustCm: "85 – 88",
  waistIn: "26.0 – 27.0",
  waistCm: "66 – 69",
  hipsIn: "36.5 – 37.5",
  hipsCm: "92 – 95",
  lengthIn: "44.5",
  lengthCm: "113"
}, {
  size: "M",
  us: "8 – 10",
  uk: "12 – 14",
  eu: "40 – 42",
  bustIn: "35.5 – 36.5",
  bustCm: "90 – 93",
  waistIn: "28.0 – 29.5",
  waistCm: "71 – 75",
  hipsIn: "38.5 – 40.0",
  hipsCm: "98 – 102",
  lengthIn: "45.0",
  lengthCm: "114"
}, {
  size: "L",
  us: "12 – 14",
  uk: "16 – 18",
  eu: "44 – 46",
  bustIn: "38.0 – 39.5",
  bustCm: "96 – 100",
  waistIn: "31.0 – 32.5",
  waistCm: "79 – 83",
  hipsIn: "41.5 – 43.0",
  hipsCm: "105 – 109",
  lengthIn: "45.5",
  lengthCm: "115"
}, {
  size: "XL",
  us: "16",
  uk: "20",
  eu: "48",
  bustIn: "41.0 – 42.5",
  bustCm: "104 – 108",
  waistIn: "34.0 – 35.5",
  waistCm: "86 – 90",
  hipsIn: "44.5 – 46.0",
  hipsCm: "113 – 117",
  lengthIn: "46.0",
  lengthCm: "116"
}];
export default function Sizing() {
  const language = useWvcLanguage();
  const t = translations[language] || translations.en;
  const [isCentimeters, setIsCentimeters] = useState(false);
  const measurementGuides = [{
    id: "bust",
    title: t.bust1,
    description: t.bustDesc
  }, {
    id: "waist",
    title: t.waist1,
    description: t.waistDesc
  }, {
    id: "hips",
    title: t.hip1,
    description: t.hipDesc
  }, {
    id: "length",
    title: t.length1,
    description: t.lengthDesc
  }];
  return <section id="尺码指南" data-nav="light" className="bg-background text-foreground py-16 md:py-24 border-b border-border">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        {/* Section Header */}
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 pb-8 border-b border-border">
          <div className="space-y-3">
            <div className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-muted-foreground font-semibold">
              <Ruler className="size-3.5 text-primary" />
              <span>{t.international}</span>
            </div>
            <h2 className="text-3xl md:text-5xl font-serif tracking-tight font-normal">
              {t.sizeGuide} <span className="italic">{t.guide}</span>
            </h2>
            <p className="text-sm md:text-base text-muted-foreground max-w-xl">
              {t.description}
            </p>
          </div>

          {/* Unit Toggle */}
          <div className="flex items-center gap-3 bg-card border border-border px-4 py-2 self-start md:self-auto shadow-xs">
            <span className={`text-xs uppercase tracking-wider font-medium transition-colors ${!isCentimeters ? "text-foreground font-semibold" : "text-muted-foreground"}`}>
              {t.inches}
            </span>
            <Switch checked={isCentimeters} onCheckedChange={setIsCentimeters} aria-label="Toggle measurement units between inches and centimeters" />
            <span className={`text-xs uppercase tracking-wider font-medium transition-colors ${isCentimeters ? "text-foreground font-semibold" : "text-muted-foreground"}`}>
              {t.centimeters}
            </span>
          </div>
        </div>

        {/* Main Grid: Table & Measuring Guide */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 pt-10 items-start">
          {/* Sizing Table (8 cols on large screens) */}
          <div className="lg:col-span-8 space-y-6">
            <div className="bg-card border border-border shadow-xs overflow-hidden">
              <div className="p-4 md:p-6 border-b border-border flex items-center justify-between">
                <div>
                  <h3 className="font-serif text-lg md:text-xl font-medium">{t.measurements}</h3>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    {t.showing} {isCentimeters ? t.inCentimeters : t.inInches}
                  </p>
                </div>
                <div className="hidden sm:flex items-center gap-2 text-xs text-muted-foreground">
                  <span className="inline-block size-2 rounded-full bg-primary" />
                  <span>{t.standardFit}</span>
                </div>
              </div>

              <Table>
                <TableHeader className="bg-muted/40">
                  <TableRow className="border-border hover:bg-transparent">
                    <TableHead className="font-semibold text-foreground py-3.5 px-4 text-xs tracking-wider uppercase">
                      {t.size}
                    </TableHead>
                    <TableHead className="font-medium text-muted-foreground py-3.5 px-3 text-xs tracking-wider uppercase">
                      {t.us}
                    </TableHead>
                    <TableHead className="font-medium text-muted-foreground py-3.5 px-3 text-xs tracking-wider uppercase">
                      {t.uk}
                    </TableHead>
                    <TableHead className="font-medium text-muted-foreground py-3.5 px-3 text-xs tracking-wider uppercase">
                      {t.eu}
                    </TableHead>
                    <TableHead className="font-medium text-foreground py-3.5 px-3 text-xs tracking-wider uppercase">
                      {t.bust}
                    </TableHead>
                    <TableHead className="font-medium text-foreground py-3.5 px-3 text-xs tracking-wider uppercase">
                      {t.waist}
                    </TableHead>
                    <TableHead className="font-medium text-foreground py-3.5 px-3 text-xs tracking-wider uppercase">
                      {t.hips}
                    </TableHead>
                    <TableHead className="font-medium text-foreground py-3.5 px-4 text-xs tracking-wider uppercase text-right">
                      {t.length}
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {sizeData.map((row, i) => <TableRow key={row.size} data-index={i} className="border-border hover:bg-muted/30 transition-colors">
                      <TableCell className="font-semibold text-foreground py-4 px-4">
                        <span className="inline-flex items-center justify-center size-7 border border-border bg-background text-xs font-mono">
                          {row.size}
                        </span>
                      </TableCell>
                      <TableCell className="text-muted-foreground py-4 px-3 text-xs font-mono">
                        {row.us}
                      </TableCell>
                      <TableCell className="text-muted-foreground py-4 px-3 text-xs font-mono">
                        {row.uk}
                      </TableCell>
                      <TableCell className="text-muted-foreground py-4 px-3 text-xs font-mono">
                        {row.eu}
                      </TableCell>
                      <TableCell className="font-medium text-foreground py-4 px-3 text-xs font-mono">
                        {isCentimeters ? `${row.bustCm} cm` : `${row.bustIn}″`}
                      </TableCell>
                      <TableCell className="font-medium text-foreground py-4 px-3 text-xs font-mono">
                        {isCentimeters ? `${row.waistCm} cm` : `${row.waistIn}″`}
                      </TableCell>
                      <TableCell className="font-medium text-foreground py-4 px-3 text-xs font-mono">
                        {isCentimeters ? `${row.hipsCm} cm` : `${row.hipsIn}″`}
                      </TableCell>
                      <TableCell className="font-medium text-foreground py-4 px-4 text-xs font-mono text-right">
                        {isCentimeters ? `${row.lengthCm} cm` : `${row.lengthIn}″`}
                      </TableCell>
                    </TableRow>)}
                </TableBody>
              </Table>
            </div>

            {/* Fit Notes Card */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="p-4 border border-border bg-card/60 space-y-1.5">
                <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-foreground">
                  <Check className="size-3.5 text-primary" />
                  <span>{t.betweenSizes}</span>
                </div>
                <p className="text-xs text-muted-foreground leading-relaxed">
                  {t.betweenSizesText}
                </p>
              </div>
              <div className="p-4 border border-border bg-card/60 space-y-1.5">
                <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-foreground">
                  <Check className="size-3.5 text-primary" />
                  <span>{t.fabricConsideration}</span>
                </div>
                <p className="text-xs text-muted-foreground leading-relaxed">
                  {t.fabricText}
                </p>
              </div>
            </div>
          </div>

          {/* Right Column: How to Measure & Atelier Concierge (4 cols) */}
          <div className="lg:col-span-4 space-y-6">
            {/* How to Measure Panel */}
            <Card className="border-border bg-card shadow-xs rounded-none">
              <CardHeader className="border-b border-border pb-4">
                <div className="flex items-center justify-between">
                  <CardTitle className="font-serif text-lg font-medium">{t.howToMeasure}</CardTitle>
                  <HelpCircle className="size-4 text-muted-foreground" />
                </div>
                <CardDescription className="text-xs text-muted-foreground">
                  {t.measureText}
                </CardDescription>
              </CardHeader>
              <CardContent className="pt-5 space-y-4">
                {measurementGuides.map((guide, i) => <div key={guide.id} data-index={i} className="space-y-1 border-b border-border/60 pb-3.5 last:border-0 last:pb-0">
                    <div className="text-xs font-semibold uppercase tracking-wider text-foreground">
                      {guide.title}
                    </div>
                    <p className="text-xs text-muted-foreground leading-relaxed">
                      {guide.description}
                    </p>
                  </div>)}
              </CardContent>
            </Card>

            {/* Personalized Concierge Box */}
            <div className="border border-border bg-background p-5 md:p-6 space-y-4 relative overflow-hidden shadow-xs">
              <div className="absolute top-0 right-0 p-3 opacity-10 pointer-events-none">
                <Sparkles className="size-16 text-primary" />
              </div>
              <div className="space-y-2">
                <div className="inline-flex items-center gap-1.5 text-xs font-semibold tracking-wider uppercase text-primary">
                  <Sparkles className="size-3.5" />
                  <span>{t.atelierAssistance}</span>
                </div>
                <h4 className="font-serif text-base md:text-lg font-medium">{t.needGuidance}</h4>
                <p className="text-xs text-muted-foreground leading-relaxed">
                  {t.atelierText}
                </p>
              </div>

              <div className="pt-1 flex flex-col gap-2.5">
                <a href="mailto:contact@yishengfushi.com" className="inline-flex items-center justify-center gap-2 text-xs font-medium bg-secondary text-secondary-foreground px-4 py-2.5 hover:bg-secondary/90 transition-colors">
                  <Mail className="size-3.5" />
                  <span>{t.emailConcierge}</span>
                </a>
                <Button asChild variant="outline" size="sm" className="w-full text-xs rounded-none">
                  <Link to="/CustomerservicePage">
                    {t.viewFaqs}
                  </Link>
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
}